import React from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { InlineMessage, KpiCard } from './ui';

/**
 * Priya's "N stores sharing" count for the vendor sell-through view
 * (`GET /vendor/sell-through/sharing`). Stores opt in from their own Store
 * Settings; while the flag is off for the vendor the API reports
 * `enabled: false` and this says sharing controls are not on yet.
 */
export const VendorSellThroughSharingCount: React.FC = () => {
  const { t } = useAppTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor-sell-through-sharing'],
    queryFn: async (): Promise<{ enabled: boolean; storeCount: number }> => {
      const response = await makeApiCall('/vendor/sell-through/sharing', { headers: await getAuthHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.message || 'Could not load sharing summary');
      return body.data;
    },
  });

  if (isError) return null;
  if (data && !data.enabled) {
    return <InlineMessage severity="info">{t('vendor.sellThroughSharing.off')}</InlineMessage>;
  }
  return (
    // One block, so the page's flex column cannot shrink the (overflow-hidden) card and clip it.
    <Box sx={{ flexShrink: 0, minWidth: 0 }}>
      <KpiCard
        label={t('vendor.sellThroughSharing.title')}
        value={data ? t('vendor.sellThroughSharing.count', { count: data.storeCount }) : ''}
        loading={isLoading}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('vendor.sellThroughSharing.copy')}
      </Typography>
    </Box>
  );
};

export default VendorSellThroughSharingCount;
